import RootStoreState from 'src/store/RootStoreState';
import { ActionContext } from 'vuex';
import { namespace } from 'vuex-class';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import {
  GQLSpecialDatesQuery,
  GQLSpecialDatesQueryVariables,
} from 'codegen/gql-types';
import ApplicationLogger from 'services/logger/ApplicationLogger';
import {
  Action,
  ActionProvider,
  ById,
  createNormalizedStore,
} from 'store/normalized-store';
import { PayloadParameter, StoreActionResult } from 'utils/store';
import SpecialDatesGql from './queries/SpecialDates.gql';

export const specialDatesNS = namespace('specialDates');

export type StoreState = ById<SpecialDate>;

export type SpecialDate = GQLSpecialDatesQuery['specialDates']['items'][0];

export type FetchAllSpecialDatesFunction = (
  payload: Omit<GQLSpecialDatesQueryVariables, 'companyId' | 'locationId'>,
) => Promise<StoreActionResult>;

type StoreActionContext = ActionContext<StoreState, RootStoreState>;

const getTagsStore = (
  graphqlClient: ApolloClient<NormalizedCacheObject>,
  logger: ApplicationLogger,
) => {
  const store = { namespaced: true };

  const fetchAll: ActionProvider<
    GQLSpecialDatesQuery,
    GQLSpecialDatesQueryVariables,
    SpecialDate
  > = (
    { rootState }: StoreActionContext,
    payload: PayloadParameter<FetchAllSpecialDatesFunction>,
  ) => {
    if (!rootState.auth.currentCompanyId) {
      throw new TypeError('currentCompanyId not provided');
    }

    if (!rootState.auth.currentLocationId) {
      throw new TypeError('currentLocationId not provided');
    }

    return {
      query: SpecialDatesGql,
      resultKey: 'specialDates',
      variables: {
        ...payload,
        companyId: rootState.auth.currentCompanyId,
        locationId: rootState.auth.currentLocationId,
      },
      useBatching: true,
    };
  };

  return createNormalizedStore<SpecialDate, StoreState, RootStoreState>({
    store,
    provide: {
      [Action.FETCH_ALL]: fetchAll,
    },
    graphqlClient,
    logger,
  });
};

export default getTagsStore;
